/**
 * packing — the canonical measurement vocabulary + a pure normalizer for a
 * product's packing (ADR-015). A packing is a `count × size` observation drawn
 * from a RetailerOffer (`packCount`, `packageSizeValue`, `packageSizeUnit`).
 *
 * Pure + portable (CP1: no platform imports, no DB). The conversion math lives
 * ONLY here (and the backend writer that calls it) — every other reader reads a
 * stored form, never converts. The vocabulary is small and bounded, so a
 * weight/volume packing can store the value in every common form of its
 * dimension cheaply.
 *
 * This replaces the defined-but-unused `measurementUnitSchema` (primitives.ts):
 * three disagreeing unit vocabularies are reconciled into the one set below.
 */

// ---------------------------------------------------------------------------
// Canonical vocabulary
// ---------------------------------------------------------------------------

/** True measures — weight + volume, with exact inter-unit conversion factors. */
export const WEIGHT_UNITS = ['g', 'kg', 'mg', 'lb', 'oz'] as const;
// Volume is fully convertible across systems (ADR-015 §5, rev. 2026-06-22): a
// household may choose to see milk in `gal`. Factors are exact US customary.
export const VOLUME_UNITS = ['mL', 'L', 'fl oz', 'gal'] as const;

/**
 * Pack-form nouns — a label, not a measure (no fixed size, not convertible). A
 * count packing stores the ONE supplied noun, with no form-set. `dozen` is the
 * sole exception: it converts to 12 `unit`.
 */
export const COUNT_FORMS = [
  'unit',
  'pack',
  'bottle',
  'box',
  'bag',
  'can',
  'jar',
  'dozen',
] as const;

export type WeightUnit = (typeof WEIGHT_UNITS)[number];
export type VolumeUnit = (typeof VOLUME_UNITS)[number];
export type CountForm = (typeof COUNT_FORMS)[number];

/**
 * The full canonical unit set — the one vocabulary replacing the retired
 * `measurementUnitSchema`. `other` is intentionally NOT a member (it is a
 * dimension, not a stored unit; an `other` packing keeps its raw verbatim unit).
 */
export const CANONICAL_UNITS = [
  ...WEIGHT_UNITS,
  ...VOLUME_UNITS,
  ...COUNT_FORMS,
] as const;

export type CanonicalUnit = (typeof CANONICAL_UNITS)[number];

/** The bounded vocabulary for `flyerdeals.pricebasis`. */
export const PRICE_BASIS_UNITS = ['lb', 'kg', 'unit'] as const satisfies readonly CanonicalUnit[];
export type PriceBasis = (typeof PRICE_BASIS_UNITS)[number];

/** True when `u` is a value allowed by `flyerdeals.pricebasis`. */
export function isPriceBasis(u: string): u is PriceBasis {
  return (PRICE_BASIS_UNITS as readonly string[]).includes(u);
}

/** True when `u` is one of the canonical units. */
export function isCanonicalUnit(u: string): u is CanonicalUnit {
  return (CANONICAL_UNITS as readonly string[]).includes(u);
}

/** A packing's dimension. `other` is the degrade-not-drop bucket (ADR-015 §12). */
export type PackingDimension = 'weight' | 'volume' | 'count' | 'other';

// ---------------------------------------------------------------------------
// Alias map — normalize known raw variants to a canonical unit. Keyed on the
// lower-cased, trimmed raw unit. A raw unit that survives the alias map AND is
// not canonical is `other` (well-formed but unrecognized).
// ---------------------------------------------------------------------------

export const UNIT_ALIASES: Readonly<Record<string, CanonicalUnit>> = {
  // weight
  g: 'g',
  gram: 'g',
  grams: 'g',
  gr: 'g',
  kg: 'kg',
  kgs: 'kg',
  kilogram: 'kg',
  kilograms: 'kg',
  mg: 'mg',
  milligram: 'mg',
  milligrams: 'mg',
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  oz: 'oz',
  ounce: 'oz',
  ounces: 'oz',
  // volume — metric is the Canadian grocery default, but volume is fully
  // convertible (ADR-015 §5) so `fl oz` / `gal` are first-class display units.
  ml: 'mL',
  millilitre: 'mL',
  millilitres: 'mL',
  milliliter: 'mL',
  milliliters: 'mL',
  l: 'L',
  litre: 'L',
  litres: 'L',
  liter: 'L',
  liters: 'L',
  'fl oz': 'fl oz',
  floz: 'fl oz',
  'fluid ounce': 'fl oz',
  'fluid ounces': 'fl oz',
  gal: 'gal',
  gallon: 'gal',
  gallons: 'gal',
  // count forms
  unit: 'unit',
  units: 'unit',
  ea: 'unit',
  each: 'unit',
  ct: 'unit',
  count: 'unit',
  counts: 'unit',
  pc: 'unit',
  pcs: 'unit',
  piece: 'unit',
  pieces: 'unit',
  pack: 'pack',
  packs: 'pack',
  pk: 'pack',
  pkg: 'pack',
  package: 'pack',
  bottle: 'bottle',
  bottles: 'bottle',
  btl: 'bottle',
  box: 'box',
  boxes: 'box',
  bag: 'bag',
  bags: 'bag',
  can: 'can',
  cans: 'can',
  tin: 'can',
  jar: 'jar',
  jars: 'jar',
  dozen: 'dozen',
  doz: 'dozen',
  dz: 'dozen',
};

// ---------------------------------------------------------------------------
// Conversion factors — value in BASE unit of the dimension, then derived forms.
// Weight base = g; volume base = mL. Exact factors only.
// ---------------------------------------------------------------------------

const WEIGHT_TO_G: Record<WeightUnit, number> = {
  mg: 0.001,
  g: 1,
  kg: 1000,
  oz: 28.349523125,
  lb: 453.59237,
};

const VOLUME_TO_ML: Record<VolumeUnit, number> = {
  mL: 1,
  L: 1000,
  // US customary, exact (matches the prototype's convVol).
  'fl oz': 29.5735,
  gal: 3785.41,
};

function dimensionOfUnit(unit: CanonicalUnit): PackingDimension {
  if ((WEIGHT_UNITS as readonly string[]).includes(unit)) return 'weight';
  if ((VOLUME_UNITS as readonly string[]).includes(unit)) return 'volume';
  return 'count';
}

// ---------------------------------------------------------------------------
// Normalizer result
// ---------------------------------------------------------------------------

/**
 * The full per-form value set for a weight/volume packing — `{ g: 454, kg: 0.454,
 * ... }`. Empty for count (no conversion). Keyed by canonical unit. Mirrors the
 * DB `forms` jsonb (`PackingForms` in schema.ts).
 */
export type PackingForms = Record<string, number>;

/** A successful normalization. */
export interface NormalizedPacking {
  ok: true;
  dimension: PackingDimension;
  /** Canonical unit (weight/volume/count) OR the raw verbatim unit for `other`. */
  normalizedUnit: string;
  /**
   * Canonical value in `normalizedUnit`. Null for a value-less loose packing
   * (e.g. bananas `lb`, no size) — the schema permits a value-less packing.
   */
  normalizedValue: number | null;
  /**
   * The full per-form value set for weight/volume; `{}` for count and `other`.
   * For `dozen`, `{ unit: 12 × value }` (the only count conversion).
   */
  forms: PackingForms;
}

/** A rejection — malformed input (size/value ≤ 0, empty unit). */
export interface RejectedPacking {
  ok: false;
  reason: 'malformed';
  detail: string;
}

export type NormalizePackingResult = NormalizedPacking | RejectedPacking;

// ---------------------------------------------------------------------------
// normalize
// ---------------------------------------------------------------------------

/**
 * Normalize a raw `(value, unit)` packing observation.
 *
 *  - **Malformed → rejection.** A present-but-non-positive value (`≤ 0`, NaN), or
 *    an empty/whitespace unit, is quarantined.
 *  - **Unknown well-formed unit → `other`.** A non-empty unit that survives the
 *    alias map but isn't canonical is typed `other`: the raw unit + value are
 *    kept verbatim, excluded from conversion/comparison.
 *  - **Weight / volume → full form-set.** The value is converted to every common
 *    form of the dimension (exact factors).
 *  - **Count → one supplied noun, no forms.** Except `dozen` = 12 `unit`.
 *  - **A value-less loose packing is allowed** (`bananas lb`): unit normalizes,
 *    `normalizedValue` is null, `forms` is `{}`.
 *
 * `rawValue` is a number or numeric string (offers carry NUMERIC-as-string), or
 * null/undefined for a loose product.
 */
export function normalizePacking(
  rawValue: number | string | null | undefined,
  rawUnit: string | null | undefined,
): NormalizePackingResult {
  const unitTrimmed = (rawUnit ?? '').trim();

  // Parse the value (null when absent).
  let value: number | null;
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    value = null;
  } else {
    const n = typeof rawValue === 'number' ? rawValue : Number(rawValue);
    if (Number.isNaN(n)) {
      return { ok: false, reason: 'malformed', detail: `non-numeric value "${rawValue}"` };
    }
    if (n <= 0) {
      return { ok: false, reason: 'malformed', detail: `non-positive value ${n}` };
    }
    value = n;
  }

  // An empty unit is malformed — a packing must name its unit (a sized packing to
  // say what the size is; a loose packing to say what it is counted/weighed in).
  if (unitTrimmed === '') {
    return { ok: false, reason: 'malformed', detail: 'empty unit' };
  }

  const aliased = UNIT_ALIASES[unitTrimmed.toLowerCase()];

  // Unknown but well-formed → `other` (degrade, never drop). Stored verbatim.
  if (aliased === undefined) {
    return {
      ok: true,
      dimension: 'other',
      normalizedUnit: unitTrimmed,
      normalizedValue: value,
      forms: {},
    };
  }

  const dimension = dimensionOfUnit(aliased);

  if (dimension === 'weight') {
    if (value === null) {
      // Loose weight product (no size) — unit only, no forms.
      return { ok: true, dimension, normalizedUnit: aliased, normalizedValue: null, forms: {} };
    }
    const grams = value * WEIGHT_TO_G[aliased as WeightUnit];
    const forms: PackingForms = {};
    for (const u of WEIGHT_UNITS) forms[u] = grams / WEIGHT_TO_G[u];
    return { ok: true, dimension, normalizedUnit: aliased, normalizedValue: value, forms };
  }

  if (dimension === 'volume') {
    if (value === null) {
      return { ok: true, dimension, normalizedUnit: aliased, normalizedValue: null, forms: {} };
    }
    const ml = value * VOLUME_TO_ML[aliased as VolumeUnit];
    const forms: PackingForms = {};
    for (const u of VOLUME_UNITS) forms[u] = ml / VOLUME_TO_ML[u];
    return { ok: true, dimension, normalizedUnit: aliased, normalizedValue: value, forms };
  }

  // count — store the one supplied noun, no form-set, EXCEPT dozen = 12 unit.
  if (aliased === 'dozen') {
    // `dozen` keeps its noun; `forms` records the 12×value `unit` equivalence
    // (the only count conversion). A bare `dozen` (no value) means one dozen = 12.
    const dozens = value ?? 1;
    return {
      ok: true,
      dimension: 'count',
      normalizedUnit: 'dozen',
      normalizedValue: value,
      forms: { unit: dozens * 12 },
    };
  }

  return {
    ok: true,
    dimension: 'count',
    normalizedUnit: aliased,
    normalizedValue: value,
    forms: {},
  };
}
